/**
 * Weekly overview built from the SAME daily rows the schedule shows:
 * one line per day for Subjects, Project Milestone (DT page), Exam Focus and Tonight's Homework.
 */
import { parseDayKey } from "../utils";
import { activityFamily } from "./weekScheduleGrid";
import { isHomeworkActivity, normalizePlanRows, withHomeworkRows } from "./homeworkRows";
import { resolveDtStep } from "../constants/dtPlaybook";

export const WEEKLY_COLUMNS = [
  "Week",
  "Days",
  "Subjects Covered",
  "Project Milestone",
  "Exam Focus",
  "Tonight's Homework",
];

const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const SHORT = { Monday: "Mon", Tuesday: "Tue", Wednesday: "Wed", Thursday: "Thu", Friday: "Fri", Saturday: "Sat", Sunday: "Sun" };

const SKIP_LINE =
  /^(problem \(short\)|problem statement|your problem|deliverable|done when|connect|youtube|article|website|video|why|outcome|purpose|tip|links?|notes?)\s*:/i;

function textOf(v) {
  return String(v ?? "").replace(/\\n/g, "\n");
}

function clip(s, n = 70) {
  const t = String(s || "").replace(/\s+/g, " ").replace(/https?:\/\/\S+/g, "").trim();
  if (!t) return "";
  if (t.length <= n) return t;
  const cut = t.slice(0, n);
  const sp = cut.lastIndexOf(" ");
  return `${(sp > 24 ? cut.slice(0, sp) : cut).replace(/[,;:\-–—(]+$/, "").trim()}…`;
}

function labeled(content, keys) {
  const raw = textOf(content);
  for (const key of keys) {
    const m = raw.match(new RegExp(`(?:^|\\n)\\s*(?:[▶•·]\\s*)?${key}\\s*:\\s*([^\\n]+)`, "i"));
    if (m && m[1].trim()) return m[1].trim().replace(/^["'“]|["'”]$/g, "");
  }
  return "";
}

function firstLine(content) {
  for (const raw of textOf(content).split("\n")) {
    const l = raw.replace(/^[▶•·\-\s]+/, "").trim();
    if (!l || l.length < 4 || /^https?:\/\//i.test(l) || SKIP_LINE.test(l)) continue;
    if (/^(lunch|break|tbd|—)$/i.test(l)) continue;
    return l.replace(/^(heading|learn today|learn|subject|topic|concept|stage|step|task)\s*:\s*/i, "");
  }
  return "";
}

const GENERIC = /^(today'?s |your project|learning|project build|system design|stand-?up|tonight|see daily)/i;

function topicOf(content, keys) {
  const v = labeled(content, keys) || firstLine(content);
  return v && !GENERIC.test(v) ? v : "";
}

function lcOf(content) {
  const c = textOf(content);
  const m =
    c.match(/LeetCode\s*#\s*(\d+)\s*[—–-]\s*([^\n(]+)/i) ||
    c.match(/Problem:\s*#\s*(\d+)\s*[—–-]\s*([^\n(]+)/i) ||
    c.match(/#\s*(\d+)\s*[—–-]\s*([^\n(]+)/);
  return m ? `LeetCode #${m[1]} ${clip(m[2], 36)}` : "";
}

function parseHomework(content) {
  const c = textOf(content);
  const out = [];
  const lc = lcOf(c);
  if (lc) out.push(lc);
  else {
    const ex = c.match(/(?:^|\n)Exercism\n([^\n]+)/i);
    if (ex) out.push(`Exercism: ${clip(ex[1], 40)}`);
  }
  const agent = c.match(/(?:^|\n)Agent Workbench\n([^\n]+)/i);
  const okr = c.match(/(?:^|\n)LinkedIn\n([^\n]+)/i);
  if (agent) out.push(`Agent Workbench: ${clip(agent[1].replace(/^SNS Agent Workbench\s*[—–-]\s*/i, ""), 42)}`);
  else if (okr) out.push(`OKRion: ${clip(okr[1], 42)}`);
  const cs = c.match(/(?:^|\n)\s*Case:\s*([^\n[]+)/i);
  if (cs) out.push(`Case: ${clip(cs[1], 40)}`);
  return out;
}

function emptyDay(dayName, dayKey) {
  return {
    dayName,
    dayKey,
    date: (String(dayKey).match(/\((\d{2}-\d{2})-\d{4}\)/) || [])[1] || "",
    learn: "",
    coding: "",
    sd: "",
    dt: null,
    project: "",
    deliverable: "",
    homework: [],
    holiday: false,
    exam: "",
    text: "",
  };
}

function collectDays(rows) {
  const weeks = new Map();
  let autoWeek = 1;
  let lastIdx = -1;
  for (const r of rows) {
    const p = parseDayKey(r[0]);
    if (!p) continue;
    let week = p.week;
    const idx = DAY_ORDER.indexOf(p.dayName);
    if (week == null) {
      if (idx !== -1 && idx <= lastIdx) autoWeek += 1;
      lastIdx = idx !== -1 ? idx : lastIdx;
      week = autoWeek;
    }
    if (!weeks.has(week)) weeks.set(week, new Map());
    const days = weeks.get(week);
    if (!days.has(p.dayName)) days.set(p.dayName, emptyDay(p.dayName, r[0]));
    const d = days.get(p.dayName);
    const act = String(r[2] || "");
    const content = textOf(r[3]);
    if (/holiday|vacation/i.test(act)) {
      d.holiday = true;
      continue;
    }
    if (isHomeworkActivity(act)) {
      if (!d.homework.length) d.homework = parseHomework(content);
      continue;
    }
    d.text += `\n${content}`;
    if (/internal assessment|au exam|mock|exam|assessment/i.test(act)) {
      d.exam = d.exam || clip(topicOf(content, ["Topic", "Focus", "Syllabus", "Heading"]) || act, 60);
      continue;
    }
    const fam = activityFamily(act);
    if (fam === "learning" && !d.learn) {
      d.learn = topicOf(content, ["Concept", "One topic today", "Learn today", "What to Learn", "Topic", "Heading", "Subject", "Learn"]);
    } else if (fam === "coding" && !d.coding) {
      d.coding = lcOf(content) || topicOf(content, ["Pattern", "Problem name", "Practice", "Concept", "Topic", "Heading"]);
    } else if (fam === "sysdesign" && !d.sd) {
      d.sd = topicOf(content, ["Component", "Concept", "What to Learn", "Topic", "Heading", "Lesson"]);
    } else if (fam === "project" || fam === "problem") {
      if (fam === "project" && !d.project) {
        d.project = topicOf(content, ["Real Project Task", "Today's project task", "Task", "Do this", "Build"]);
        d.deliverable = labeled(content, ["Deliverable", "Done when"]);
      }
    }
  }
  return weeks;
}

function dayLines(days, fn) {
  return days
    .map((d) => {
      const body = d.holiday ? "Holiday" : fn(d);
      return body ? `▶ ${SHORT[d.dayName] || d.dayName} — ${body}` : null;
    })
    .filter(Boolean)
    .join("\n");
}

/**
 * @param {object} daily   plans.daily ({ rows })
 * @param {object} saved   plans.weekly (optional) — exam-question lines are kept
 */
export function buildWeeklyFromDaily(daily, saved = null) {
  const rows = withHomeworkRows(normalizePlanRows(daily?.rows || []));
  if (!rows.length) return null;
  const weeks = collectDays(rows);
  if (!weeks.size) return null;

  const savedExam = new Map();
  (saved?.rows || []).forEach((r) => {
    const w = Number((String(r?.[0] || "").match(/(\d+)/) || [])[1]);
    const lines = textOf(r?.[4])
      .split("\n")
      .map((l) => l.replace(/^▶\s*/, "").trim())
      .filter((l) => /^[A-Z][\w &/().-]{2,40}:\s*\S/.test(l) && !/^(notes|dt|project|system design|placement|revise)\b/i.test(l));
    if (w && lines.length) savedExam.set(w, lines.slice(0, 2));
  });

  let globalDay = 0;
  const outRows = [...weeks.keys()]
    .sort((a, b) => a - b)
    .map((week) => {
      const days = [...weeks.get(week).values()].sort(
        (a, b) => DAY_ORDER.indexOf(a.dayName) - DAY_ORDER.indexOf(b.dayName)
      );
      days.forEach((d) => {
        d.dt = d.holiday ? null : resolveDtStep(d.text, globalDay);
        if (!d.holiday) globalDay += 1;
      });
      const working = days.filter((d) => !d.holiday);

      const daysCell = [
        `${working.length} working day${working.length === 1 ? "" : "s"}`,
        days.map((d) => `${SHORT[d.dayName]}${d.date ? ` ${d.date}` : ""}`).join(" · "),
      ].join("\n");

      const subjects = dayLines(days, (d) => {
        const bits = [];
        if (d.learn) bits.push(clip(d.learn, 60));
        if (d.coding) bits.push(`DSA: ${clip(d.coding, 40)}`);
        if (d.sd) bits.push(`SD: ${clip(d.sd, 40)}`);
        if (!bits.length && d.dt?.step) bits.push(`DT: ${d.dt.step}`);
        return bits.join(" · ") || "Learning slot (see day)";
      });

      const milestone = dayLines(days, (d) => {
        const page = d.dt?.page ? `DT p.${d.dt.page}${d.dt.step ? ` “${d.dt.step}”` : ""}` : "";
        const task = clip(d.project, 70);
        const del = d.deliverable ? ` → ${clip(d.deliverable, 50)}` : "";
        return [page, task].filter(Boolean).join(": ") + del || "Project Build slot";
      });

      const exam = [
        dayLines(days, (d) => {
          if (d.exam) return `Exam: ${d.exam}`;
          const what = d.learn || d.dt?.step || "";
          const bits = [];
          if (what) bits.push(`explain “${clip(what, 45)}” with an example from your project`);
          if (d.coding) bits.push(`re-solve ${clip(d.coding, 30)}`);
          return bits.join("; ");
        }),
        ...(savedExam.get(week) || []).map((l) => `▶ ${l}`),
      ]
        .filter(Boolean)
        .join("\n");

      const homework = dayLines(days, (d) => (d.homework.length ? d.homework.join(" · ") : ""));

      return [`Week ${week}`, daysCell, subjects, milestone, exam || "—", homework || "—"];
    });

  return {
    module: "weekly",
    title: "Weekly Schedule (synced from Daily)",
    columns: WEEKLY_COLUMNS,
    rows: outRows,
    source: "daily-sync",
  };
}
